import {Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {catchError, map, take} from 'rxjs/operators';
import {AuthService} from './auth.service';
import {ApiService} from './api.service';
import {CommonUtils} from "../utils/common";

/**
 * Guard zarządzający kontrolą dostępu do modułu V-Messages.
 * Weryfikuje po stronie serwera API, czy zalogowany użytkownik posiada uprawnienia
 * systemowe (RBAC) do odczytu i wysyłki wiadomości.
 */
@Injectable({
  providedIn: 'root'
})
export class VMessagesAccessGuard implements CanLoad, CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router,
    private apiService: ApiService,
    public commonUtils: CommonUtils
  ) {
  }

  /**
   * Blokuje lub zezwala na asynchroniczne pobranie (Lazy Loading) kodu JavaScript
   * modułu wiadomości. Stanowi warstwę ochrony prewencyjnej, zapobiegając transferowi
   * logiki biznesowej do przeglądarki klienta przed autoryzacją na serwerze API.
   *
   * @param route Konfiguracja chronionej ścieżki.
   * @param segments Segmenty adresu URL.
   * @returns Observable emitujący wartość logiczną (true w przypadku posiadania odpowiedniej roli).
   */
  canLoad(
    route: Route,
    segments: UrlSegment[]
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.apiService.post('/dashboard/v-messages-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      console.log('check - v-messages - ok');
      return true;
    }));
  }

  /**
   * Weryfikuje uprawnienia w momencie próby fizycznej aktywacji i wyrenderowania
   * interfejsu modułu wiadomości. Zabezpiecza spójność sesji przechwytując
   * jej wygaśnięcie (HTTP 401), a brak odpowiednich uprawnień domenowych
   * obsługuje asynchronicznie za pomocą klasy narzędziowej CommonUtils.
   *
   * @param route Migawka aktywowanej ścieżki.
   * @param state Migawka stanu routera.
   * @returns Observable emitujący wartość logiczną lub bezpieczne drzewo routingu (UrlTree).
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.apiService.post('/dashboard/v-messages-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      console.log('check - canActivate - v-messages - ok', response);

      return this.commonUtils.handleHttpResponseShowInfoToast(response, '', 'Nie masz uprawnień do tej strony')
    }));
  }

}
